import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, Download, Heart, LockKeyhole, LogOut, Mail, ShieldCheck, Trash2, UserRound, WalletCards } from "lucide-react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

import SEO from "../components/SEO";
import GoogleIdentityButton from "../components/GoogleIdentityButton";
import PremiumCompetitionManager from "../components/PremiumCompetitionManager";
import PremiumAlerts from "../components/PremiumAlerts";
import PremiumWebBilling from "../components/PremiumWebBilling";
import { premiumPersonalManagerEnabled } from "../components/PremiumYourOdds";
import { useConsumerAuth } from "../context/ConsumerAuthContext";
import api from "../lib/api";
import consumerApi from "../lib/consumerApi";
import { writePrivacyPreferences } from "../lib/privacyPreferences";


const baseSections = [
  { slug: "", label: "Account", icon: UserRound },
  { slug: "security", label: "Security", icon: LockKeyhole },
  { slug: "subscription", label: "Subscription", icon: WalletCards },
  { slug: "favourites", label: "Favourite operators", icon: Heart },
  { slug: "privacy", label: "Privacy & data", icon: ShieldCheck },
  { slug: "alerts", label: "Alerts", icon: Bell },
];
const inputClass = "mt-2 w-full rounded border border-white/15 bg-white/5 px-3 py-2.5 text-sm outline-none focus:border-[var(--cc-gold)]";

export default function ConsumerAccount() {
  const auth = useConsumerAuth();
  const location = useLocation();
  const billingReturn = new URLSearchParams(location.search || "").get("billing");
  const section = ["success", "cancelled"].includes(billingReturn) ? "subscription" : location.pathname.split("/")[2] || "";
  const sections = premiumPersonalManagerEnabled()
    ? [...baseSections, { slug: "competitions", label: "My competitions", icon: Heart }]
    : baseSections;
  if (auth.ready && !auth.account) return <Navigate to="/signin" replace />;
  if (!auth.ready || !auth.account) return <div className="min-h-[60vh] px-6 py-16 text-center text-sm text-white/55" role="status">Loading your account...</div>;
  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-6 lg:px-8" data-testid="consumer-account">
      <SEO title="My account" description="Manage your Competition Checker account." path="/account" noIndex />
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-6">
        <div><p className="overline text-[var(--cc-gold)]">My account</p><h1 className="mt-2 font-display text-3xl font-semibold">Your Competition Checker</h1></div>
        <button type="button" onClick={auth.logout} className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white"><LogOut size={17} />Sign out</button>
      </div>
      <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
        <nav aria-label="Account sections" className="flex gap-2 overflow-x-auto lg:flex-col">
          {sections.map(({ slug, label, icon: Icon }) => <Link key={slug} to={`/account${slug ? `/${slug}` : ""}`} className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded px-3 py-2 text-sm ${section === slug ? "bg-[var(--cc-gold)] text-[var(--cc-navy)]" : "text-white/65 hover:bg-white/5 hover:text-white"}`}><Icon size={17} />{label}</Link>)}
        </nav>
        <main className="min-w-0">{section === "security" ? <SecurityPanel /> : section === "subscription" ? <PremiumWebBilling returnState={billingReturn} /> : section === "favourites" ? <FavouritesPanel /> : section === "privacy" ? <PrivacyPanel /> : section === "alerts" ? <PremiumAlerts /> : section === "competitions" ? <PremiumCompetitionManager /> : <AccountPanel />}</main>
      </div>
    </div>
  );
}

function PanelTitle({ title, description }) {
  return <header className="mb-6"><h2 className="font-display text-2xl font-semibold">{title}</h2><p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--cc-text-muted)]">{description}</p></header>;
}

function AccountPanel() {
  const { account, mutate, refresh } = useConsumerAuth();
  const [username, setUsername] = useState(account.user.username || "");
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const updateUsername = async (event) => {
    event.preventDefault();
    try {
      await mutate("patch", "/me/username", { username });
      await refresh();
      toast.success("Username updated.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Username change failed.");
    }
  };
  const request = async (event) => {
    event.preventDefault();
    try { await mutate("post", "/me/email", { new_email: newEmail, current_password: password }); toast.success("Check your new email to confirm the change."); setNewEmail(""); setPassword(""); } catch (error) { toast.error(error?.response?.data?.detail || "Email change failed."); }
  };
  return (
    <section>
      <PanelTitle title="Account" description="Your account ID remains the same across the website and Android app." />
      <dl className="grid gap-4 border-y border-white/10 py-5 text-sm sm:grid-cols-2">
        <div><dt className="text-white/45">Username</dt><dd className="mt-1 break-all">{account.user.username || "Not set"}</dd></div>
        <div><dt className="text-white/45">Email</dt><dd className="mt-1 break-all">{account.user.email}</dd></div>
        <div><dt className="text-white/45">Verification</dt><dd className="mt-1">{account.user.email_verified ? "Verified" : "Pending"}</dd></div>
        <div><dt className="text-white/45">Sign-in methods</dt><dd className="mt-1">{[account.user.has_password && "Password", account.user.google_linked && "Google"].filter(Boolean).join(" and ")}</dd></div>
      </dl>
      <form onSubmit={updateUsername} className="mt-8 max-w-lg space-y-4">
        <h3 className="font-display text-lg font-semibold">Change username</h3>
        <label className="block text-sm">Username<input className={inputClass} type="text" autoComplete="username" minLength={3} maxLength={30} required value={username} onChange={(event) => setUsername(event.target.value)} /></label>
        <button className="btn-gold rounded px-4 py-2.5 text-sm">Save username</button>
      </form>
      {account.user.has_password ?
        <form onSubmit={request} className="mt-8 max-w-lg space-y-4 border-t border-white/10 pt-7">
          <h3 className="font-display text-lg font-semibold">Change email</h3>
          <label className="block text-sm">New email<input className={inputClass} type="email" autoComplete="email" required value={newEmail} onChange={(event) => setNewEmail(event.target.value)} /></label>
          <label className="block text-sm">Current password<input className={inputClass} type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /></label>
          <button className="btn-gold rounded px-4 py-2.5 text-sm">Send confirmation</button>
        </form> :
        <p className="mt-8 max-w-lg border-t border-white/10 pt-7 text-sm text-[var(--cc-text-muted)]">Email changes for Google-only accounts are not available here. Your account and Google account are separate; contact support if your Google email changes.</p>}
    </section>
  );
}

function SecurityPanel() {
  const auth = useConsumerAuth();
  const { mutate } = auth;
  const [sessions, setSessions] = useState([]);
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [linkPassword, setLinkPassword] = useState("");
  const [linkActive, setLinkActive] = useState(false);
  useEffect(() => {
    consumerApi.get("/me/sessions").then(({ data }) => setSessions(data)).catch(() => toast.error("Sessions could not be loaded."));
  }, []);
  const change = async (event) => { event.preventDefault(); try { const data = await auth.mutate("post", "/me/password", form); auth.setCsrfToken(data.csrf_token); await auth.refresh(); setForm({ current_password: "", new_password: "" }); toast.success("Password changed and other sessions signed out."); } catch (error) { toast.error(error?.response?.data?.detail || "Password change failed."); } };
  const signOutAll = async () => { const data = await auth.mutate("post", "/consumer/auth/logout-all"); auth.setAccount(false); auth.setCsrfToken(""); toast.success(`${data.sessions_revoked} sessions signed out.`); };
  const revoke = async (sessionId) => { await auth.mutate("delete", `/me/sessions/${sessionId}`); setSessions((current) => current.filter((session) => session.session_id !== sessionId)); };
  const createLinkChallenge = useCallback(() => mutate(
    "post", "/me/google/link/challenge", { current_password: linkPassword }
  ), [mutate, linkPassword]);
  const finishLink = async (challengeId, credential) => {
    await auth.mutate("post", "/me/google/link/complete", {
      challenge_id: challengeId, id_token: credential,
    });
    setLinkActive(false);
    setLinkPassword("");
    await auth.refresh();
    toast.success("Google linked to this account.");
  };
  return (
    <section>
      <PanelTitle title="Security" description="Manage sign-in methods and active sessions." />
      {auth.account.user.has_password ?
        <form onSubmit={change} className="max-w-lg space-y-4">
          <h3 className="font-display text-lg font-semibold">Change password</h3>
          <p className="text-sm text-[var(--cc-text-muted)]">Changing your password signs out other sessions.</p>
          <label className="block text-sm">Current password<input className={inputClass} type="password" autoComplete="current-password" required value={form.current_password} onChange={(event) => setForm({ ...form, current_password: event.target.value })} /></label>
          <label className="block text-sm">New password<input className={inputClass} type="password" autoComplete="new-password" minLength={12} required value={form.new_password} onChange={(event) => setForm({ ...form, new_password: event.target.value })} /></label>
          <button className="btn-gold rounded px-4 py-2.5 text-sm">Change password</button>
        </form> :
        <p className="text-sm text-[var(--cc-text-muted)]">You sign in with Google. No local password is set.</p>}
      {auth.account.user.has_password && !auth.account.user.google_linked &&
        <div className="mt-8 max-w-lg border-t border-white/10 pt-6">
          <h3 className="font-display text-lg font-semibold">Link Google</h3>
          <p className="mt-2 text-sm text-[var(--cc-text-muted)]">Confirm your current password, then choose the matching Google account. Accounts are never linked by email alone.</p>
          <label className="mt-4 block text-sm">Current password<input className={inputClass} type="password" autoComplete="current-password" value={linkPassword} onChange={(event) => { setLinkPassword(event.target.value); setLinkActive(false); }} /></label>
          {linkActive ? <div className="mt-4"><GoogleIdentityButton createChallenge={createLinkChallenge} onCredential={finishLink} /></div> :
            <button type="button" className="mt-4 rounded border border-white/20 px-4 py-2 text-sm" disabled={!linkPassword} onClick={() => setLinkActive(true)}>Continue to Google</button>}
        </div>}
      <div className="mt-10 border-t border-white/10 pt-6">
        <div className="flex items-center justify-between gap-4"><h3 className="font-display text-lg font-semibold">Active sessions</h3><button type="button" className="text-sm text-[var(--cc-gold)]" onClick={signOutAll}>Sign out all</button></div>
        <ul className="mt-4 divide-y divide-white/10">{sessions.map((session) => <li key={session.session_id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"><span>{session.current ? "This session" : "Other session"}</span><span className="text-white/45">Started {new Date(session.created_at).toLocaleDateString("en-GB")}</span>{!session.current && <button type="button" className="text-[var(--cc-gold)]" onClick={() => revoke(session.session_id)}>Revoke</button>}</li>)}</ul>
      </div>
    </section>
  );
}

function FavouritesPanel() {
  const { account, mutate, refresh } = useConsumerAuth();
  const [operators, setOperators] = useState([]);
  const [selected, setSelected] = useState("");
  useEffect(() => { api.get("/operators").then(({ data }) => setOperators(data.filter((operator) => operator.public_eligible))); }, []);
  const favourites = account.favourite_operators || [];
  const favouriteKeys = new Set(favourites.map((item) => item.operator_key));
  const available = operators.filter(
    (operator) => !favouriteKeys.has(operator.operator_public_key)
  );
  const add = async (event) => { event.preventDefault(); if (!selected) return; await mutate("post", "/me/favourite-operators", { operator_key: selected }); await refresh(); setSelected(""); };
  const remove = async (key) => { await mutate("delete", `/me/favourite-operators/${key}`); await refresh(); };
  return <section><PanelTitle title="Favourite operators" description="Keep a private shortlist of the operators you follow." /><form onSubmit={add} className="flex max-w-xl flex-col gap-3 sm:flex-row"><label className="sr-only" htmlFor="favourite-operator">Operator</label><select id="favourite-operator" className="min-h-10 flex-1 rounded border border-white/15 bg-[var(--cc-navy-2)] px-3 text-sm" value={selected} onChange={(e) => setSelected(e.target.value)}><option value="">Choose an operator</option>{available.map((operator) => <option key={operator.operator_public_key} value={operator.operator_public_key}>{operator.name}</option>)}</select><button className="btn-gold rounded px-4 py-2 text-sm" disabled={!selected}>Add favourite</button></form><ul className="mt-6 divide-y divide-white/10">{favourites.length ? favourites.map((item) => <li className="flex items-center justify-between py-4" key={item.operator_key}><span>{item.name}</span><button type="button" className="text-sm text-white/55 hover:text-white" onClick={() => remove(item.operator_key)}>Remove</button></li>) : <li className="py-5 text-sm text-white/45">No favourite operators yet.</li>}</ul></section>;
}

const privacyOptions = [
  ["service_analytics", "Service analytics", "Help improve aggregate service performance. Off by default."],
  ["advertising_personalisation", "Advertising personalisation", "Allow personalised advertising signals. Off by default."],
  ["marketing_email", "Marketing email", "Receive occasional Competition Checker updates. Off by default."],
  ["anonymous_usage_insight_sharing", "Anonymous usage insights", "Allow inclusion in future aggregate insights. External sharing remains disabled."],
];

function PrivacyPanel() {
  const auth = useConsumerAuth();
  const { account, mutate, refresh } = auth;
  const [deletion, setDeletion] = useState({ current_password: "", confirmation: "" });
  const createDeleteChallenge = useCallback(() => mutate(
    "post", "/me/google/delete/challenge"
  ), [mutate]);
  const toggle = async (field, value) => {
    await mutate("patch", "/me/privacy", { [field]: value });
    if (["service_analytics", "advertising_personalisation"].includes(field)) {
      writePrivacyPreferences({ ...account.privacy, [field]: value });
    }
    await refresh();
  };
  const exportData = async () => {
    try {
      const payload = await mutate("post", "/me/export");
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "competition-checker-data.json";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Your data export is ready.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Data export failed.");
    }
  };
  const deleteAccount = async (event) => {
    event.preventDefault();
    try {
      await mutate("post", "/me/delete-account", deletion);
      auth.setAccount(false);
      auth.setCsrfToken("");
      toast.success("Your account has been deleted.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Account deletion failed.");
    }
  };
  const deleteWithGoogle = async (challengeId, credential) => {
    await mutate("post", "/me/google/delete/complete", {
      challenge_id: challengeId,
      id_token: credential,
      confirmation: deletion.confirmation,
    });
    auth.setAccount(false);
    auth.setCsrfToken("");
    toast.success("Your account has been deleted.");
  };
  return (
    <section>
      <PanelTitle title="Privacy & data" description="Necessary account and security storage is always available. Every optional purpose is separate and can be changed here." />
      <div className="border-y border-white/10 py-5"><div className="flex items-center justify-between gap-4"><div><h3 className="font-medium">Strictly necessary</h3><p className="mt-1 text-sm text-white/45">Authentication, session security, and saved privacy choices.</p></div><span className="text-xs font-semibold text-[var(--cc-gold)]">Always on</span></div></div>
      <div className="divide-y divide-white/10">{privacyOptions.map(([field, label, description]) => <label key={field} className="flex cursor-pointer items-center justify-between gap-6 py-5"><span><span className="block font-medium">{label}</span><span className="mt-1 block text-sm leading-relaxed text-white/45">{description}</span></span><input className="h-5 w-5 shrink-0 accent-[var(--cc-gold)]" type="checkbox" checked={account.privacy[field]} onChange={(e) => toggle(field, e.target.checked)} /></label>)}</div>
      <div className="mt-9 border-t border-white/10 pt-7">
        <h3 className="font-display text-lg font-semibold">Export your data</h3>
        <p className="mt-2 text-sm leading-relaxed text-white/45">Download a machine-readable copy of your account, favourites and privacy history. Passwords, tokens and other users are excluded.</p>
        <button type="button" className="mt-4 inline-flex items-center gap-2 rounded border border-white/20 px-4 py-2 text-sm" onClick={exportData}><Download size={17} />Export my data</button>
      </div>
      <form className="mt-9 border-t border-red-400/25 pt-7" onSubmit={deleteAccount}>
        <h3 className="font-display text-lg font-semibold text-red-200">Delete account</h3>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/45">This revokes every session, removes your email and optional account data, and cannot restore this account. A minimal pseudonymous security tombstone and audit may be retained for justified security or legal purposes.</p>
        <div className="mt-4 grid max-w-xl gap-4">
          {account.user.has_password && <label className="block text-sm">Current password<input className={inputClass} type="password" autoComplete="current-password" required value={deletion.current_password} onChange={(e) => setDeletion({ ...deletion, current_password: e.target.value })} /></label>}
          <label className="block text-sm">Type DELETE MY ACCOUNT<input className={inputClass} type="text" autoComplete="off" required value={deletion.confirmation} onChange={(e) => setDeletion({ ...deletion, confirmation: e.target.value })} /></label>
          {account.user.has_password && <button type="submit" className="inline-flex w-fit items-center gap-2 rounded border border-red-400/40 px-4 py-2 text-sm text-red-200" disabled={deletion.confirmation !== "DELETE MY ACCOUNT"}><Trash2 size={17} />Delete my account</button>}
        </div>
      </form>
      {account.user.google_linked && deletion.confirmation === "DELETE MY ACCOUNT" &&
        <div className="mt-4 max-w-xl"><p className="mb-3 text-sm text-white/55">Or confirm deletion with a fresh Google sign-in.</p><GoogleIdentityButton createChallenge={createDeleteChallenge} onCredential={deleteWithGoogle} /></div>}
    </section>
  );
}

export function ConsumerEmailConfirmation() {
  const auth = useConsumerAuth();
  const [state, setState] = useState("working");
  const attempted = useRef(false);
  const { account, mutate, ready, refresh, setCsrfToken } = auth;
  useEffect(() => {
    if (!ready || !account || attempted.current) return;
    attempted.current = true;
    const token = new URLSearchParams(
      window.location.hash.replace(/^#/, "")
    ).get("token") || window.__CC_CONSUMER_ACTION_TOKEN__ || "";
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
    if (!token) {
      setState("invalid");
      return;
    }
    mutate("post", "/me/email/confirm", { token })
      .then((data) => {
        setCsrfToken(data.csrf_token);
        return refresh();
      })
      .then(() => setState("done"))
      .catch(() => setState("invalid"))
      .finally(() => { delete window.__CC_CONSUMER_ACTION_TOKEN__; });
  }, [account, mutate, ready, refresh, setCsrfToken]);
  if (auth.ready && !auth.account) return <Navigate to="/signin" replace />;
  return <div className="mx-auto max-w-xl px-6 py-20 text-center"><Mail className="mx-auto text-[var(--cc-gold)]" size={30} /><h1 className="mt-5 font-display text-3xl font-semibold">{state === "done" ? "Email changed" : state === "invalid" ? "Email change unavailable" : "Confirming your email"}</h1><p className="mt-3 text-sm text-[var(--cc-text-muted)]">{state === "done" ? "Your sign-in email has been updated without changing your account identity." : state === "invalid" ? "This email-change link is invalid or has expired." : "Please wait while we confirm your new address."}</p>{state !== "working" && <Link className="mt-6 inline-block text-[var(--cc-gold)]" to="/account">Return to account</Link>}</div>;
}
